/**
 * SITECAT-1 · 上架中心「手机站」·「首页分类」页签（样稿 designs/mockups/consumer-site/首页分类-样稿.html ① ②）。
 * 做法照「首页横幅」：卡片列表（拖动 / 上下移 / 删除 / 上线下线）+ 列表下方展开的编辑面板 + 草稿 + 整组「保存」才 PUT。
 * 接口：GET / PUT /api/v1/site/categories（权限 listing.mappings.manage，整组有序替换）· 可选线路 = GET /site/products-status 里 showing=true。
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Building2, Bus, Camera, Clock, Flame, GripVertical, Mountain, Plane, Plus, Route, Snowflake, Sun, Ticket, Users, X, type LucideIcon } from "lucide-react";
import { Can } from "@/auth/permissions";
import { SITE_QK, getSiteCategories, putSiteCategories, type SiteCategory, type SiteProductStatus } from "@/services/site";
import { useReorder } from "./useReorder";

export const CATEGORY_ICON_MAP: Record<string, LucideIcon> = {
  route: Route, clock: Clock, users: Users, plane: Plane, mountain: Mountain, "building-2": Building2, camera: Camera, ticket: Ticket, bus: Bus, flame: Flame, sun: Sun, snowflake: Snowflake,
};
const NAME_MAX = 6;

interface Draft { id?: string; name_cn: string; name_en: string; icon: string; active: boolean; product_ids: string[] }

function Pill({ tone, children }: { tone: "green" | "gray" | "red"; children: React.ReactNode }) {
  const cls = { green: "bg-emerald-50 text-emerald-600", gray: "bg-slate-100 text-slate-600", red: "bg-rose-50 text-rose-600" }[tone];
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}><span className="h-[7px] w-[7px] rounded-full bg-current" />{children}</span>;
}

const toDraft = (c: SiteCategory): Draft => ({ id: c.id, name_cn: c.name_cn, name_en: c.name_en ?? "", icon: c.icon, active: c.active, product_ids: c.products.map((p) => p.product_id) });

export function CategoriesTab({ statusItems, onSaved }: { statusItems: SiteProductStatus[]; onSaved: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: SITE_QK.categories, queryFn: getSiteCategories });
  const [draft, setDraft] = useState<Draft[] | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);   // 正在编辑的卡片（-1 = 新增）
  const [panel, setPanel] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [panelErr, setPanelErr] = useState<string | null>(null);
  const max = q.data?.max ?? 8;
  const icons = q.data?.icons ?? Object.keys(CATEGORY_ICON_MAP);
  const items = draft ?? (q.data?.items ?? []).map(toDraft);
  const dirty = draft !== null;
  const { move, dragProps } = useReorder(items, (v) => setDraft(v));
  const statusById = useMemo(() => new Map(statusItems.map((s) => [s.product_id, s])), [statusItems]);
  const showingIds = useMemo(() => new Set(statusItems.filter((s) => s.showing).map((s) => s.product_id)), [statusItems]);
  const showingOf = (d: Draft) => d.product_ids.filter((id) => showingIds.has(id)).length;
  const saveMut = useMutation({
    mutationFn: () => putSiteCategories(items.map((d) => ({ id: d.id, name_cn: d.name_cn.trim(), name_en: d.name_en.trim() || null, icon: d.icon, active: d.active, product_ids: d.product_ids }))),
    onSuccess: (d) => { qc.setQueryData(SITE_QK.categories, d); setDraft(null); setEditIdx(null); setPanel(null); setErr(null); onSaved(); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  const edit = (i: number, patch: Partial<Draft>) => setDraft(items.map((c, k) => (k === i ? { ...c, ...patch } : c)));

  function openPanel(i: number) {
    setEditIdx(i); setPanelErr(null);
    setPanel(i === -1 ? { name_cn: "", name_en: "", icon: icons[0] ?? "route", active: true, product_ids: [] } : { ...items[i], product_ids: [...items[i].product_ids] });
  }
  function confirmPanel() {
    if (!panel || editIdx === null) return;
    const nm = panel.name_cn.trim();
    if (!nm) return setPanelErr("名称不能为空");
    if (nm.length > NAME_MAX) return setPanelErr(`名称最多 ${NAME_MAX} 个字`);
    if (items.some((c, k) => k !== editIdx && c.name_cn.trim() === nm)) return setPanelErr("名称重复");
    if (panel.product_ids.length === 0) return setPanelErr("至少勾 1 条线路");
    if (editIdx === -1 && items.length >= max) return setPanelErr(`已有 ${max} 个，不能再加`);
    const next = { ...panel, name_cn: nm };
    setDraft(editIdx === -1 ? [...items, next] : items.map((c, k) => (k === editIdx ? next : c)));
    setEditIdx(null); setPanel(null); setPanelErr(null);
  }

  if (q.isLoading) return <p className="text-sm text-slate-400">加载中…</p>;
  if (q.isError) return <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button>;

  return (
    <div className="flex flex-col gap-3" data-testid="categories-tab">
      <p className="text-[12.5px] text-slate-500">最多 {max} 个 · 拖动排顺序（手机上每行 4 个）· 点卡片编辑名称、图标和线路 · 改完点「保存」左边马上刷新</p>
      {items.length === 0 && <p className="rounded-lg bg-slate-50 px-3 py-2 text-[12.5px] text-slate-500" data-testid="categories-empty">还没配分类：手机站首页现在按产品类型自动显示（多日游 / 一日游 / 小团 / 接送机）。配了第一个起，首页就完全按这里配置显示。</p>}
      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        {items.map((c, i) => {
          const total = c.product_ids.length;
          const showing = showingOf(c);
          const gone = total - showing;
          const Icon = CATEGORY_ICON_MAP[c.icon] ?? Route;
          const zero = showing === 0;
          return (
            <div key={`${c.id ?? "new"}-${i}`} {...dragProps(i)} data-testid="category-card"
              className={`flex flex-col gap-1.5 rounded-[10px] border bg-white p-3 text-[12.5px] ${editIdx === i ? "border-blue-500 ring-1 ring-blue-200" : zero || gone > 0 ? "border-rose-300" : "border-slate-200"}`}>
              <div className="flex items-center gap-1.5 text-slate-400">
                <GripVertical size={13} className="cursor-grab" /><span>拖动</span>
                <Can perm="listing.mappings.manage">
                  <span className="ml-auto flex gap-0.5">
                    <button type="button" aria-label="上移" onClick={() => move(i, i - 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowUp size={12} /></button>
                    <button type="button" aria-label="下移" onClick={() => move(i, i + 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowDown size={12} /></button>
                    <button type="button" aria-label="删除分类" onClick={() => setDraft(items.filter((_, k) => k !== i))} className="rounded p-0.5 hover:bg-rose-50 hover:text-rose-600"><X size={12} /></button>
                  </span>
                </Can>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon size={18} /></span>
                <div className="min-w-0 flex-1">
                  <b className="block truncate text-[14px] text-slate-900" data-testid="category-name">{c.name_cn}</b>
                  <span className="text-slate-500" data-testid="category-count">
                    {zero ? <span className="text-rose-600">0 条在展示</span> : <>{total} 条线路{gone > 0 && <>，<span className="text-rose-600">{gone} 条已不在手机站展示</span></>}</>}
                  </span>
                </div>
              </div>
              {zero && <p className="text-[11.5px] text-rose-600">没有在展示的线路 · 自动不显示</p>}
              <div className="flex items-center justify-between">
                <Can perm="listing.mappings.manage">
                  <button type="button" onClick={() => openPanel(i)} data-testid="category-edit" className="rounded-md border border-slate-300 px-2.5 py-0.5 text-[12px] text-slate-600 hover:bg-slate-50">{editIdx === i ? "编辑中" : "编辑"}</button>
                  <button type="button" onClick={() => edit(i, { active: !c.active })} aria-pressed={c.active} data-testid="category-active">{c.active ? <Pill tone="green">上线</Pill> : <Pill tone="gray">下线</Pill>}</button>
                </Can>
              </div>
            </div>
          );
        })}
        {items.length < max && (
          <Can perm="listing.mappings.manage">
            <button type="button" onClick={() => openPanel(-1)} data-testid="category-add"
              className="flex min-h-[112px] flex-col items-center justify-center rounded-[10px] border border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-600">
              <Plus size={16} />添加分类<span className="text-[11px]">还能加 {max - items.length} 个</span>
            </button>
          </Can>
        )}
      </div>

      {panel && (
        <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50/40 p-3 text-[12.5px] animate-in fade-in slide-in-from-bottom-2" data-testid="category-panel">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1">名称（中文，必填，最多 {NAME_MAX} 个字）
              <input value={panel.name_cn} onChange={(e) => setPanel({ ...panel, name_cn: e.target.value })} maxLength={20} aria-label="分类名称" className="rounded border border-slate-300 px-2 py-1.5 text-sm" /></label>
            <label className="flex flex-col gap-1">英文名（选填，切到 EN 时显示，没填就显示中文）
              <input value={panel.name_en} onChange={(e) => setPanel({ ...panel, name_en: e.target.value })} maxLength={60} aria-label="英文名" className="rounded border border-slate-300 px-2 py-1.5 text-sm" /></label>
          </div>
          <div>
            <span>图标</span>
            <div className="mt-1 flex flex-wrap gap-1.5" role="radiogroup" aria-label="图标">
              {icons.map((k) => { const I = CATEGORY_ICON_MAP[k]; return I ? (
                <button key={k} type="button" role="radio" aria-checked={panel.icon === k} aria-label={`图标 ${k}`} onClick={() => setPanel({ ...panel, icon: k })}
                  className={`flex h-9 w-9 items-center justify-center rounded-xl border ${panel.icon === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}><I size={18} /></button>) : null; })}
            </div>
          </div>
          <ProductPicker statusItems={statusItems} statusById={statusById} ids={panel.product_ids} onChange={(ids) => setPanel({ ...panel, product_ids: ids })} />
          {panelErr && <p className="text-xs text-rose-600" role="alert" data-testid="category-panel-error">{panelErr}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setPanel(null); setEditIdx(null); setPanelErr(null); }} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12.5px]">取消</button>
            <button type="button" onClick={confirmPanel} data-testid="category-confirm" className="rounded-md bg-blue-600 px-3 py-1 text-[12.5px] font-medium text-white">确定（回到上面，再点「保存」生效）</button>
          </div>
        </div>
      )}

      {err && <p className="text-xs text-rose-600" data-testid="categories-error">{err}</p>}
      <Can perm="listing.mappings.manage">
        <div className="flex justify-end gap-2">
          <button type="button" disabled={!dirty} onClick={() => { setDraft(null); setEditIdx(null); setPanel(null); setErr(null); }} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px] disabled:opacity-50">取消</button>
          <button type="button" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="categories-save" className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">{saveMut.isPending ? "保存中…" : "保存"}</button>
        </div>
      </Can>
    </div>
  );
}

/** 线路双栏：左 = 在手机站展示中的线路（可搜、点选、全选）；右 = 已选（拖动排序 / 清空；已不在展示的标红但允许保留）。 */
function ProductPicker({ statusItems, statusById, ids, onChange }: {
  statusItems: SiteProductStatus[]; statusById: Map<string, SiteProductStatus>; ids: string[]; onChange: (ids: string[]) => void;
}) {
  const [kw, setKw] = useState("");
  const showing = statusItems.filter((s) => s.showing);
  const shown = showing.filter((s) => !kw.trim() || `${s.title}${s.code}`.toLowerCase().includes(kw.trim().toLowerCase()));
  const { move, dragProps } = useReorder(ids, onChange);
  const picked = new Set(ids);
  const toggle = (id: string) => onChange(picked.has(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-white p-2.5">
        <div className="flex items-center justify-between"><b>可选（在手机站展示中的 {showing.length} 条）</b>
          <button type="button" onClick={() => onChange([...ids, ...shown.filter((s) => !picked.has(s.product_id)).map((s) => s.product_id)])} className="text-blue-600 hover:underline" data-testid="picker-all">全选</button></div>
        <input value={kw} onChange={(e) => setKw(e.target.value)} placeholder="搜名称或编号" aria-label="搜线路" className="rounded border border-slate-300 px-2 py-1 text-[12.5px]" />
        <div className="max-h-[260px] overflow-auto">
          {shown.map((s) => (
            <label key={s.product_id} className="flex cursor-pointer items-start gap-2 border-b border-slate-100 py-1.5" data-testid="picker-option">
              <input type="checkbox" checked={picked.has(s.product_id)} onChange={() => toggle(s.product_id)} className="mt-0.5" aria-label={`勾选 ${s.title}`} />
              <span>{s.title}<span className="block text-[11px] text-slate-400">{s.code}</span></span>
            </label>
          ))}
          {shown.length === 0 && <p className="py-4 text-center text-slate-400">没有可选的线路</p>}
        </div>
        <p className="text-[11px] text-slate-400">没在展示的线路不能新勾选（先去「线路展示清单」补齐）</p>
      </div>
      <div className="flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-white p-2.5">
        <div className="flex items-center justify-between"><b>已选 {ids.length} 条 · 拖动排顺序</b>
          <button type="button" onClick={() => onChange([])} className="text-slate-500 hover:text-rose-600" data-testid="picker-clear">清空</button></div>
        <div className="max-h-[300px] overflow-auto">
          {ids.map((id, i) => {
            const s = statusById.get(id);
            const out = !s || !s.showing;
            return (
              <div key={id} {...dragProps(i)} className={`flex items-center gap-1.5 border-b border-slate-100 py-1.5 ${out ? "text-rose-600" : ""}`} data-testid="picker-selected">
                <GripVertical size={12} className="cursor-grab text-slate-400" /><span className="w-5 text-slate-400">{i + 1}</span>
                <span className="min-w-0 flex-1">{s?.title ?? "（线路）"}{out && <span className="ml-1 text-[11px]">已不在展示</span>}<span className="block text-[11px] text-slate-400">{s?.code ?? ""}</span></span>
                <button type="button" aria-label="上移" onClick={() => move(i, i - 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowUp size={12} /></button>
                <button type="button" aria-label="下移" onClick={() => move(i, i + 1)} className="rounded p-0.5 hover:bg-slate-100"><ArrowDown size={12} /></button>
                <button type="button" aria-label="移出" onClick={() => toggle(id)} className="rounded p-0.5 hover:bg-rose-50 hover:text-rose-600"><X size={12} /></button>
              </div>
            );
          })}
          {ids.length === 0 && <p className="py-4 text-center text-slate-400">还没勾线路（至少 1 条）</p>}
        </div>
      </div>
    </div>
  );
}
